<template>
  <article class="bg-transparent transition-colors hover:bg-white/[0.025]">
    <!-- One line per pick: rank, team and frozen line on the left, how it is
         going on the right. The detail sits behind the chevron. -->
    <div class="flex items-center gap-2 px-2 py-1.5">
      <span
        class="flex h-5 w-5 shrink-0 items-center justify-center rounded-full border border-brand-green/50 bg-brand-green/10 text-[10px] font-black tabular-nums text-brand-green"
        :aria-label="`Pick ${position}`"
      >
        {{ position }}
      </span>

      <TeamLogoChip
        :team="pickedTeam.name"
        :abbr="pickedTeam.abbr"
        :logo="pickedTeam.logo"
        size="xs"
      />

      <button
        type="button"
        class="flex min-w-0 flex-1 items-center gap-2 text-left"
        :aria-expanded="expanded"
        :aria-label="`${expanded ? 'Collapse' : 'Expand'} ${pickedTeam.abbr} pick details`"
        @click="expanded = !expanded"
      >
        <span class="flex min-w-0 flex-1 items-baseline gap-1.5">
          <span class="shrink-0 text-sm font-bold text-white">{{ pickedTeam.abbr }}</span>
          <span class="shrink-0 text-sm font-black tabular-nums text-white">{{ selectedFrozenLine }}</span>
          <span class="truncate text-xs text-slate-400">{{ matchupLabel }}</span>
          <svg
            v-if="submitted"
            class="h-3 w-3 shrink-0 self-center text-slate-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            stroke-width="2"
            aria-label="Locked"
            role="img"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              d="M16 12V8a4 4 0 00-8 0v4m-1 0h10a1 1 0 011 1v6a1 1 0 01-1 1H6a1 1 0 01-1-1v-6a1 1 0 011-1z"
            />
          </svg>
        </span>

        <span class="flex shrink-0 items-center gap-1 text-[11px] font-medium tabular-nums">
          <span v-if="result" class="font-bold" :class="resultClass">
            {{ resultLabel }}
          </span>
          <template v-else-if="live">
            <span class="font-bold text-amber-300">{{ live.score }}</span>
            <span class="text-slate-500">·</span>
            <span
              class="font-semibold"
              :class="live.margin == null ? 'text-amber-300' : live.margin > 0 ? 'text-brand-green' : live.margin < 0 ? 'text-rose-400' : 'text-slate-300'"
            >{{ live.label }}</span>
          </template>
          <!-- Win % and the band word, the same as Home's "Your Picks"
               (PickedGameRow). It used to print "Edge" beside the consensus
               point gap, which is display-only and nothing ranks on. -->
          <template v-else-if="summary">
            <span :class="bandClass">{{ summary.win }}</span>
            <span class="text-slate-500">·</span>
            <span :class="bandClass">{{ summary.band }}</span>
            <template v-if="summary.tie">
              <span class="text-slate-500">·</span>
              <span class="text-slate-400">{{ summary.tie }}</span>
            </template>
          </template>
          <span v-else-if="edge.side" class="text-slate-400">
            Edge on {{ edgeSideAbbr }}
          </span>
          <span v-else-if="edge.frozen == null" class="text-slate-500">Not frozen</span>
          <span v-else class="text-slate-500">No market</span>
        </span>

        <svg
          class="h-4 w-4 shrink-0 text-slate-400 transition-transform"
          :class="expanded ? 'rotate-180' : ''"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          aria-hidden="true"
        >
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      <button
        v-if="overLimit && !result && !submitted"
        type="button"
        class="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-slate-500 transition-colors hover:bg-rose-500/10 hover:text-rose-300 active:bg-rose-500/20"
        :aria-label="`Remove ${pickedTeam.abbr} from your picks`"
        :title="`Remove ${pickedTeam.abbr} pick`"
        @click="$emit('remove')"
      >
        <svg class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>

    <div v-show="expanded" class="border-t border-white/[0.07] bg-black/15">
      <div class="flex items-center justify-between gap-3 px-3 py-2 text-xs text-slate-400">
        <span>{{ kickoff }}</span>
        <NetworkBadge :broadcast="game.broadcast" :week="game.week" />
      </div>

      <div class="flex items-center justify-between gap-3 border-y border-white/[0.07] px-3 py-2.5">
        <span class="text-[10px] font-semibold uppercase tracking-[0.1em] text-slate-500">
          Line movement
        </span>
        <span class="whitespace-nowrap text-xs font-semibold tabular-nums">
          <span class="text-slate-300">Frozen {{ selectedFrozenLine }}</span>
          <span class="mx-1.5 text-slate-600" aria-hidden="true">→</span>
          <span :class="edge.market == null ? 'text-slate-500' : 'text-brand-green'">
            Market {{ selectedMarketLine }}
          </span>
        </span>
      </div>

      <!-- The Home card's one line (EdgeVerdict), same markup: value, the key
           number crossed (only when our number really wins it), how the
           books lean, the tie chance. Equal centred columns; `min-w-max`
           lets a long item take what it needs on a phone. Shown at every
           width — "crosses N" used to be hidden below sm. -->
      <p
        v-if="lineItems.length && !result"
        class="flex items-center whitespace-nowrap border-b border-white/[0.07] px-3 py-2 text-[10px] tracking-tight tabular-nums text-slate-400 sm:text-[11px] sm:tracking-normal"
      >
        <span
          v-for="item in lineItems"
          :key="item.key"
          class="min-w-max flex-1 basis-0 text-center"
          :class="item.key === 'value' ? valueClass : ''"
        >{{ item.text }}</span>
      </p>

      <div class="flex flex-wrap items-center justify-between gap-2 px-3 py-2.5">
        <p class="min-w-0 flex-1 truncate text-xs text-slate-500">
          <template v-if="result">Final result: <span :class="resultClass">{{ resultLabel }}</span></template>
          <template v-else-if="submitted">Locked — it's on the commissioner's sheet</template>
          <template v-else-if="!game.fivePicksConfirmed">Frozen line is not confirmed yet</template>
          <template v-else-if="!lineItems.length">Frozen line confirmed</template>
        </p>
        <div v-if="!result && !submitted" class="flex shrink-0 items-center gap-2">
          <button
            type="button"
            class="rounded-lg border border-rose-500/20 px-2.5 py-1.5 text-xs font-semibold text-rose-300 transition-colors hover:bg-rose-500/10 active:bg-rose-500/20"
            @click="$emit('remove')"
          >
            Remove
          </button>
        </div>
      </div>
    </div>
  </article>
</template>

<script>
import TeamLogoChip from "./TeamLogoChip.vue";
import NetworkBadge from "./NetworkBadge.vue";
import { computeEdge, formatSigned } from "../utils/edge.js";
import { BAND_TEXT_CLASS, pickedSummary, verdictLine } from "../utils/verdictLine.js";
import { livePickStatus } from "../utils/weekGames.js";

export default {
  name: "PickCard",
  components: { TeamLogoChip, NetworkBadge },
  props: {
    game: { type: Object, required: true },
    position: { type: Number, required: true },
    // The week is over five picks: show an always-visible remove control in
    // the row so trimming back down doesn't mean expanding every pick.
    overLimit: { type: Boolean, default: false },
  },
  emits: ["remove"],
  data() {
    return { expanded: false };
  },
  computed: {
    submitted() {
      return Boolean(this.game.pick?.submitted_at);
    },
    pickedSide() {
      return this.game.pick?.side || "home";
    },
    pickedTeam() {
      return this.pickedSide === "home" ? this.game.homeTeam : this.game.awayTeam;
    },
    opponentTeam() {
      return this.pickedSide === "home" ? this.game.awayTeam : this.game.homeTeam;
    },
    matchupLabel() {
      return `${this.pickedSide === "home" ? "vs" : "@"} ${this.opponentTeam.abbr}`;
    },
    edge() {
      return computeEdge(this.game);
    },
    result() {
      return this.game.pick?.result || null;
    },
    // Score and cover while the game is on; null otherwise (then the row shows
    // the result, or the pregame win % and band).
    live() {
      return livePickStatus(this.game, this.pickedSide);
    },
    // "53.4% · Solid" (+ "8.8% tie"), or null for a pick against the edge
    // side, which reads "Edge on X" instead.
    summary() {
      return pickedSummary(this.edge, this.pickedSide);
    },
    // Coloured by band, the same as Home: a Flat pick is not green.
    bandClass() {
      return BAND_TEXT_CLASS[this.summary?.bandKey] ?? "text-slate-300";
    },
    edgeSideAbbr() {
      if (!this.edge.side) return "";
      return this.edge.side === "home" ? this.game.homeTeam.abbr : this.game.awayTeam.abbr;
    },
    selectedFrozenLine() {
      return this.selectedLine(this.edge.frozen);
    },
    selectedMarketLine() {
      return this.selectedLine(this.edge.market);
    },
    // The Home card's one line — see `verdictLine`. "crosses N" there only
    // appears when our number really wins it and line value is positive.
    // `verdictLine` describes the model's side, so it is shown only when that
    // is the side picked: under a pick against the edge ("Edge on X") its
    // value, crosses and books-lean would all belong to the other team.
    lineItems() {
      return this.summary ? verdictLine(this.game, this.edge) : [];
    },
    // The same emphasis EdgeVerdict gives the value item.
    valueClass() {
      return this.edge.lineValueProbability >= 0.05
        ? "font-semibold text-slate-300"
        : "font-semibold text-slate-500";
    },
    kickoff() {
      const [when = "", date = ""] = String(this.game.gameDate).split(" - ");
      return date ? `${when} · ${date}` : when;
    },
    resultLabel() {
      if (this.result === "win") return "Won";
      if (this.result === "loss") return "Lost";
      if (this.result === "push") return "Push";
      return "";
    },
    resultClass() {
      if (this.result === "win") return "text-brand-green";
      if (this.result === "loss") return "text-rose-400";
      return "text-slate-300";
    },
  },
  methods: {
    selectedLine(homeLine) {
      if (homeLine == null) return "—";
      return formatSigned(this.pickedSide === "home" ? homeLine : -homeLine);
    },
  },
};
</script>
